import React, { useEffect, useId, useRef, useState } from 'react';
import {
  useLionAutoTemplates, useSaveLionAutoTemplate, useUpdateLionAutoTemplate, useDeleteLionAutoTemplate,
} from '../../hooks/useLionAutoTemplates';
import {
  saveableGroups, defaultTemplateName, templateErrorText, templateErrorInfo, templateReadRetryable, templateStampText,
  templateGroupsTitle, saveResultText, deleteConfirmText, deleteResultText,
} from '../../utils/lionAutoTemplates';
import { easternText } from '../../utils/lionAutoRunner';
import LaTemplateSaveForm from './LaTemplateSaveForm';
import LaTemplateApplyList from './LaTemplateApplyList';

// Pricing groups → "Templates": this artist's Lion Auto templates (a show's
// groups saved once, applied to the other dates). The list shows each one's
// venue, groups, version, last save and how many of this show's groups are
// linked to it ("N here"), with Apply to shows… (anyone who can edit this
// show's groups), and Update from this show and Delete (the artist floor's
// permission: canEdit). "Save this show as a template" opens the save form;
// Apply to shows… the show list (LaTemplateApplyList), which applies one
// show at a time. The modal can't close while a save, delete or apply runs
// (each button it holds off says why), and takes the focus on every view
// switch. A write with an unknown outcome reads in warn style (reload to
// check). A template is held by id only; its data always comes from the list
// query (one deleted elsewhere says so). Every text from Skybox or the
// templates renders as text.
//
// Props: eventId (the show on screen); groups (its saved groups); onClose().

const LIST = { kind: 'list' };
const SAVE_DISABLED_TITLE = "This show has no group that can go in a template (Lion Checker groups and other templates' groups can't)";
const BUSY_TITLE = 'Wait for the save, delete or apply in progress to finish';
// The footer's error line: an unknown outcome in warn style, a refusal in red
const errorClass = (kind) => (kind === 'warn' ? 'la-template-unknown' : 'ai-inline-error');

function TemplateRow({ t, canEdit, busy, deleting, canUpdate, onApply, onUpdate, onDelete }) {
  const groupCount = Array.isArray(t.groups) ? t.groups.length : 0;
  const here = Number(t.linkedHere) || 0;
  return (
    <tr>
      <td className="la-import-name" title={t.name}>{t.name}</td>
      <td className="la-import-name" title={t.venue || undefined}>{t.venue || '—'}</td>
      <td className="num" title={templateGroupsTitle(t) || undefined}>{groupCount}</td>
      <td className="num">{t.version ?? '?'}</td>
      <td className="num" title="This show's groups linked to this template">{here ? `${here} here` : ''}</td>
      <td title={easternText(t.updatedAt) || undefined}>{templateStampText(t)}</td>
      <td className="la-template-actions-cell">
        <span className="la-template-actions">
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            disabled={busy}
            title={busy ? BUSY_TITLE : undefined}
            onClick={() => onApply(t)}
          >
            Apply to shows…
          </button>
          {canEdit && (
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              disabled={busy || !canUpdate}
              title={busy ? BUSY_TITLE : (canUpdate ? "Re-save it from this show's groups (version + 1)" : SAVE_DISABLED_TITLE)}
              onClick={() => onUpdate(t)}
            >
              Update from this show
            </button>
          )}
          {canEdit && (
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              disabled={busy}
              title={busy ? BUSY_TITLE : undefined}
              onClick={() => onDelete(t)}
            >
              {deleting ? 'Deleting…' : 'Delete'}
            </button>
          )}
        </span>
      </td>
    </tr>
  );
}

// The list view's body: loading, a failed read (Retry when worth it: a 503,
// a 5xx, no answer), an unknown performer, no templates yet, or the table
function TemplateList({ query, groups, canEdit, busy, deletingId, onApply, onUpdate, onDelete }) {
  const { data, isLoading, isError, error, isFetching, refetch } = query;
  const templates = Array.isArray(data?.templates) ? data.templates : [];
  const performerName = data?.performerName || null;
  const retryButton = templateReadRetryable(error) && (
    <button
      type="button"
      className="btn btn-secondary btn-sm la-status-action"
      onClick={() => refetch()}
      disabled={isFetching}
      title={isFetching ? 'Reading the templates…' : undefined}
    >
      {isFetching ? 'Retrying…' : 'Retry'}
    </button>
  );
  if (isLoading) return <div className="la-empty">Reading this artist&apos;s templates…</div>;
  if (isError && !data) {
    return (
      <div className={`la-status${templateReadRetryable(error) ? ' error' : ''}`}>
        {templateErrorText(error, { read: true })}{retryButton}
      </div>
    );
  }
  if (!performerName) {
    return (
      <div className="la-empty">
        This show&apos;s performer is not known, so it can&apos;t have templates. Build its groups by hand.
      </div>
    );
  }
  return (
    <>
      <div className="la-import-show">
        <strong title={performerName}>{performerName}</strong>
        {data.venue && <span className="la-muted">this show: {data.venue}</span>}
        {isFetching && <span className="la-hint">Refreshing…</span>}
      </div>
      {isError && (
        <div className="la-status error">
          Could not refresh the templates (showing the last list): {templateErrorText(error, { read: true })}
          {retryButton}
        </div>
      )}
      {!templates.length ? (
        <div className="la-empty">No templates for {performerName} yet.</div>
      ) : (
        <table className="la-import-table la-template-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Venue</th>
              <th className="num">Groups</th>
              <th className="num">Version</th>
              <th className="num">Here</th>
              <th>Updated</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {templates.map(t => (
              <TemplateRow
                key={t.id}
                t={t}
                canEdit={canEdit}
                busy={busy}
                deleting={deletingId != null && String(deletingId) === String(t.id)}
                canUpdate={saveableGroups(groups, t.id).some(r => !r.reason)}
                onApply={onApply}
                onUpdate={onUpdate}
                onDelete={onDelete}
              />
            ))}
          </tbody>
        </table>
      )}
      {!canEdit && (
        <div className="la-import-warnings">
          <span className="la-hint">
            Only an admin, or a pricer assigned to {performerName}, can save, update or delete its templates.
            You can apply them to the shows you price.
          </span>
        </div>
      )}
    </>
  );
}

function LaTemplatesModal({ eventId, groups = [], onClose }) {
  const titleId = useId();
  const dialogRef = useRef(null);
  const closeRef = useRef(null);
  // { kind: 'list' } | { kind: 'save' } | { kind: 'update', id } | { kind: 'apply', id }
  const [view, setView] = useState(LIST);
  const [notice, setNotice] = useState(null); // the last save, update or delete in words
  const [applying, setApplying] = useState(false);
  const listQ = useLionAutoTemplates(eventId);
  const saveM = useSaveLionAutoTemplate(eventId);
  const updateM = useUpdateLionAutoTemplate(eventId);
  const deleteM = useDeleteLionAutoTemplate(eventId);

  const busy = applying || saveM.isPending || updateM.isPending || deleteM.isPending;
  const data = listQ.data;
  const templates = Array.isArray(data?.templates) ? data.templates : [];
  const performerName = data?.performerName || null;
  const canEdit = !!data?.canEdit && !!performerName;
  const current = view.id != null ? templates.find(t => String(t.id) === String(view.id)) || null : null;
  const canSave = canEdit && saveableGroups(groups).some(r => !r.reason);

  const handleClose = () => {
    if (!busy) onClose();
  };
  closeRef.current = handleClose;

  // Escape closes (not while a write or an apply is in flight)
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      e.stopPropagation();
      closeRef.current?.();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  // Focus the dialog on open; give it back to the opener on close
  useEffect(() => {
    const opener = document.activeElement;
    dialogRef.current?.focus();
    return () => {
      if (opener && opener.isConnected && typeof opener.focus === 'function') opener.focus();
    };
  }, []);

  // A view switch unmounts the button that made it: the dialog takes the
  // focus back, so the keyboard stays inside it
  useEffect(() => {
    dialogRef.current?.focus();
  }, [view]);

  const openView = (next) => {
    if (busy) return;
    saveM.reset();
    updateM.reset();
    deleteM.reset();
    setNotice(null);
    setView(next);
  };
  const backToList = () => openView(LIST);

  const handleSave = ({ name, groupIds }) => {
    saveM.mutate({ name, groupIds }, {
      onSuccess: (answer) => {
        setView(LIST);
        setNotice(saveResultText(answer, 'save'));
      },
    });
  };
  const handleUpdate = ({ name, groupIds }) => {
    if (!current) return;
    updateM.mutate({ id: current.id, name, groupIds }, {
      onSuccess: (answer) => {
        setView(LIST);
        setNotice(saveResultText(answer, 'update'));
      },
    });
  };
  const handleDelete = (t) => {
    if (busy || !window.confirm(deleteConfirmText(t))) return;
    setNotice(null);
    deleteM.mutate(t.id, { onSuccess: (answer) => setNotice(deleteResultText(answer, t.name)) });
  };

  const deleteError = deleteM.isError ? templateErrorInfo(deleteM.error) : null;
  const listFooter = (
    <div className="modal-footer bulk-review-footer">
      {notice && <span className="ai-admin-note" aria-live="polite">{notice}</span>}
      {deleteError && <span className={errorClass(deleteError.kind)} aria-live="polite">{deleteError.text}</span>}
      <span style={{ flex: 1 }} />
      <button type="button" className="btn-secondary" onClick={handleClose} disabled={busy} title={busy ? BUSY_TITLE : undefined}>
        Close
      </button>
      {canEdit && (
        <button
          type="button"
          className="btn-primary"
          onClick={() => openView({ kind: 'save' })}
          disabled={busy || !canSave}
          title={busy ? BUSY_TITLE : (canSave ? "Save this show's groups as a new template for every show of this artist" : SAVE_DISABLED_TITLE)}
        >
          Save this show as a template
        </button>
      )}
    </div>
  );

  // An update opened on a template the list no longer has (deleted elsewhere)
  const goneView = (
    <>
      <div className="modal-body la-template-body">
        <div className="la-empty">This template is no longer on the list (deleted?). Go back to the list.</div>
      </div>
      <div className="modal-footer bulk-review-footer">
        <span style={{ flex: 1 }} />
        <button type="button" className="btn-secondary" onClick={backToList} disabled={busy} title={busy ? BUSY_TITLE : undefined}>
          Back
        </button>
      </div>
    </>
  );

  let title = 'Templates';
  let content;
  if (view.kind === 'save') {
    title = 'Save this show as a template';
    content = (
      <LaTemplateSaveForm
        mode="save"
        groups={groups}
        defaultName={defaultTemplateName(performerName, data?.venue)}
        busy={saveM.isPending}
        error={saveM.error}
        onSubmit={handleSave}
        onBack={backToList}
      />
    );
  } else if (view.kind === 'update') {
    title = 'Update the template from this show';
    content = !current ? goneView : (
      <LaTemplateSaveForm
        mode="update"
        template={current}
        groups={groups}
        busy={updateM.isPending}
        error={updateM.error}
        onSubmit={handleUpdate}
        onBack={backToList}
      />
    );
  } else if (view.kind === 'apply') {
    title = 'Apply the template to shows';
    content = <LaTemplateApplyList template={current} onBack={backToList} onRunningChange={setApplying} />;
  } else {
    content = (
      <>
        <div className="modal-body la-template-body">
          <TemplateList
            query={listQ}
            groups={groups}
            canEdit={canEdit}
            busy={busy}
            deletingId={deleteM.isPending ? deleteM.variables : null}
            onApply={(t) => openView({ kind: 'apply', id: t.id })}
            onUpdate={(t) => openView({ kind: 'update', id: t.id })}
            onDelete={handleDelete}
          />
        </div>
        {listFooter}
      </>
    );
  }

  return (
    <div className="modal-overlay" onClick={busy ? undefined : handleClose}>
      <div
        ref={dialogRef}
        className="modal modal-content"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        style={{ maxWidth: 980, width: '95%' }}
        onClick={e => e.stopPropagation()}
      >
        <div className="modal-header">
          <h2 id={titleId} style={{ margin: 0 }}>{title}</h2>
          <button
            type="button"
            className="modal-close"
            onClick={handleClose}
            disabled={busy}
            title={busy ? BUSY_TITLE : undefined}
            aria-label="Close"
          >
            ×
          </button>
        </div>
        {content}
      </div>
    </div>
  );
}

export default LaTemplatesModal;
